// src/services/adminDataService.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { getCurrentAuthUser } from "./authDataService";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

const FUNCTIONS_REGION = "europe-west3";
const functions = getFunctions(undefined, FUNCTIONS_REGION);

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const requireAuthUser = async () => {
  const user = await getCurrentAuthUser();

  if (!user?.uid) {
    const err = new Error("unauthenticated");
    err.code = "unauthenticated";
    throw err;
  }

  if (auth?.currentUser?.getIdToken) {
  await auth.currentUser.getIdToken(true);
}

  return user;
};

const callFunctionData = async (name, data) => {
  console.log("🟣 Function call START", { name, data });

  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      const err = new Error("server-timeout");
      err.code = "deadline-exceeded";
      reject(err);
    }, 15000);
  });

  try {
    const isNative =
      window.Capacitor?.getPlatform?.() === "ios" ||
      window.Capacitor?.getPlatform?.() === "android";

    if (isNative) {
  const tokenResult = await FirebaseAuthentication.getIdToken({
    forceRefresh: true,
  });

  const token = tokenResult?.token;

  if (!token) {
    throw new Error("native-token-missing");
  }

  const result = await Promise.race([
    fetch(
      `https://${FUNCTIONS_REGION}-edens-ernte-2f682.cloudfunctions.net/${name}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data }),
      }
    ),
    timeoutPromise,
  ]);

  const json = await result.json();

  if (!result.ok || json?.error) {
    const err = new Error(json?.error?.message || "function-error");
    err.code = json?.error?.status || "internal";
    throw err;
  }

  return { data: json.result };
}


    const callable = httpsCallable(functions, name);

    const result = await Promise.race([callable(data), timeoutPromise]);

    console.log("🟣 Function call WEB RESULT", result);
    return result;
  } catch (error) {
    console.error("🟣 Function call FAILED", {
      name,
      code: error?.code || null,
      message: error?.message || null,
      details: error?.details || null,
      full: JSON.stringify(error, Object.getOwnPropertyNames(error)),
    });

    throw error;
  }
};

const normalizeUserFromCallable = (res, fallbackEmail = "") => {
  const data = res?.data || {};
  const payload = data?.result || data;
  const userObj = payload?.user || null;

  if (!userObj?.uid) {
    return {
      uid: "",
      email: fallbackEmail || "",
      claims: {
        admin: false,
        superadmin: false,
      },
    };
  }

  const claims = userObj?.claims || userObj?.customClaims || {};

  return {
    uid: userObj.uid,
    email: userObj.email || fallbackEmail || "",
    claims: {
      admin: claims.admin === true,
      superadmin: claims.superadmin === true,
    },
  };
};

export const humanizeAdminError = (error) => {
  const code = String(error?.code || "");
  const msg = String(error?.message || "");

  if (code.includes("deadline-exceeded")) return "Serveranfrage dauert zu lange.";
  if (msg.includes("server-timeout")) return "Serveranfrage dauert zu lange.";

  if (code.includes("unauthenticated")) return "Du bist nicht eingeloggt.";
  if (code.includes("permission-denied")) return "Kein Zugriff (nur Superadmin).";
  if (code.includes("not-found")) return "Nutzer nicht gefunden.";
  if (code.includes("invalid-argument")) return "Ungültige Eingabe.";
  if (code.includes("failed-precondition")) return "Aktion nicht erlaubt.";
  if (code.includes("internal")) return "Serverfehler (internal).";

  if (msg.includes("unauthenticated")) return "Du bist nicht eingeloggt.";
  if (msg.includes("permission-denied")) return "Kein Zugriff (nur Superadmin).";
  if (msg.includes("not-found")) return "Nutzer nicht gefunden.";
  if (msg.includes("invalid-argument")) return "Ungültige Eingabe.";
  if (msg.includes("failed-precondition")) return "Aktion nicht erlaubt.";

  return msg || "Unbekannter Fehler";
};

export const findAdminUserByEmail = async (email) => {
  const mail = normalizeEmail(email);

  if (!mail || !mail.includes("@")) {
    const err = new Error("invalid-argument");
    err.code = "invalid-argument";
    throw err;
  }

  await requireAuthUser();

  const res = await callFunctionData("adminGetUserByEmailCallable", {
    email: mail,
  });

  return normalizeUserFromCallable(res, mail);
};

export const setUserRoleData = async ({ targetUid, role, enabled }) => {
  if (!targetUid) {
    const err = new Error("invalid-argument");
    err.code = "invalid-argument";
    throw err;
  }

  if (role !== "admin" && role !== "superadmin") {
    const err = new Error("invalid-argument");
    err.code = "invalid-argument";
    throw err;
  }

  await requireAuthUser();

  await callFunctionData("setUserRole", {
    targetUid,
    role,
    enabled: !!enabled,
  });

  return { ok: true };
};

export const setAdminRoleData = async ({ targetUid, enabled }) => {
  return setUserRoleData({
    targetUid,
    role: "admin",
    enabled,
  });
};

export const setSuperAdminRoleData = async ({ targetUid, enabled }) => {
  return setUserRoleData({
    targetUid,
    role: "superadmin",
    enabled,
  });
};